/**
 * Limites de validacao de um campo (FORM-39) — funcoes puras.
 *
 * `CampoFormulario.validacao` era aplicada pelo servidor, mas nenhuma tela a
 * mostrava: o visitante so descobria o limite pelo 400. Agora o painel do
 * campo edita os limites e o FormRenderer aplica os mesmos antes de enviar,
 * com a mesma mensagem de `formularios.services._validar_valor_campo`.
 *
 * Contrato (default `{}` = sem limite):
 *   - NUMERO: `min` / `max` (numero);
 *   - TEXTO, TEXTAREA, TELEFONE: `min_len` (inteiro >= 0) / `max_len`
 *     (inteiro >= 1), contados no texto sem espacos nas pontas.
 * `regex` saiu do contrato; o servidor recusa chave que nao e' do tipo.
 */

export const LIMITES_NUMERICOS = ["min", "max"];
export const LIMITES_DE_TAMANHO = ["min_len", "max_len"];
const TIPOS_COM_LIMITE_DE_TAMANHO = ["TEXTO", "TEXTAREA", "TELEFONE"];
const MINIMO_NA_DEFINICAO = { min_len: 0, max_len: 1 };
const NUMERO_DECIMAL = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i;

export const ROTULO_DO_LIMITE = {
  min: "Valor mínimo",
  max: "Valor máximo",
  min_len: "Mínimo de caracteres",
  max_len: "Máximo de caracteres",
};

/** As chaves de `validacao` que valem para o tipo (em ordem: menor, maior). */
export function limitesDoTipo(tipo) {
  if (tipo === "NUMERO") return LIMITES_NUMERICOS;
  if (TIPOS_COM_LIMITE_DE_TAMANHO.includes(tipo)) return LIMITES_DE_TAMANHO;
  return [];
}

function estaVazio(valor) {
  return valor === null || valor === undefined || (typeof valor === "string" && valor.trim() === "");
}

// Mesma leitura do servidor (`_decimal_do_limite`): numero JSON, ou texto
// "10" / "10,5". O resto nao e' numero.
function numeroDoLimite(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== "string") return null;
  const texto = valor.trim().replace(",", ".");
  return NUMERO_DECIMAL.test(texto) ? Number(texto) : null;
}

function inteiroDoLimite(valor, minimo) {
  const numero = numeroDoLimite(valor);
  return numero !== null && Number.isInteger(numero) && numero >= minimo ? numero : null;
}

function limiteNormalizado(chave, valor) {
  return chave in MINIMO_NA_DEFINICAO
    ? inteiroDoLimite(valor, MINIMO_NA_DEFINICAO[chave])
    : numeroDoLimite(valor);
}

/**
 * `validacao` pronta para o POST /campos: so as chaves do tipo atual (o que
 * sobrou de outro tipo sai), numero como numero e limite vazio de fora.
 * Valor ilegivel segue como veio, para o servidor recusar nomeando o campo.
 */
export function limitesParaPayload(tipo, validacao) {
  const regras = validacao && typeof validacao === "object" ? validacao : {};
  return limitesDoTipo(tipo).reduce((saida, chave) => {
    const valor = regras[chave];
    if (estaVazio(valor)) return saida;
    const numero = limiteNormalizado(chave, valor);
    return { ...saida, [chave]: numero === null ? valor : numero };
  }, {});
}

/** Erro dos limites digitados no painel do campo, ou "". */
export function erroNosLimites(tipo, validacao) {
  const regras = validacao && typeof validacao === "object" ? validacao : {};
  const chaves = limitesDoTipo(tipo);
  const invalida = chaves.find(
    (chave) => !estaVazio(regras[chave]) && limiteNormalizado(chave, regras[chave]) === null
  );
  if (invalida) {
    return invalida in MINIMO_NA_DEFINICAO
      ? `${ROTULO_DO_LIMITE[invalida]}: use um número inteiro de ${MINIMO_NA_DEFINICAO[invalida]} em diante.`
      : `${ROTULO_DO_LIMITE[invalida]}: informe um número.`;
  }
  const [menor, maior] = chaves;
  const limites = limitesParaPayload(tipo, regras);
  if (menor in limites && maior in limites && limites[menor] > limites[maior]) {
    return `${ROTULO_DO_LIMITE[menor]} não pode ser maior que o ${ROTULO_DO_LIMITE[maior].toLowerCase()}.`;
  }
  return "";
}

/** 0.5 -> "0,5"; 100 -> "100": como o servidor escreve o limite na mensagem. */
export function formatarLimite(numero) {
  return String(numero).replace(".", ",");
}

/**
 * Erro de `min`/`max` contra um numero JA lido; "" quando passa. Separado do
 * `erroDeLimiteNoEnvio` porque o campo de dinheiro (FORM-03) le o valor com
 * outra regua (pt-BR) e depois aplica os mesmos limites.
 */
export function erroDoIntervaloNumerico(campo, numero) {
  const label = campo.label || campo.tipo;
  const regras = campo.validacao && typeof campo.validacao === "object" ? campo.validacao : {};
  const minimo = numeroDoLimite(regras.min);
  const maximo = numeroDoLimite(regras.max);
  if (minimo !== null && numero < minimo) return `${label}: mínimo ${formatarLimite(minimo)}.`;
  if (maximo !== null && numero > maximo) return `${label}: máximo ${formatarLimite(maximo)}.`;
  return "";
}

/**
 * Erro de um valor PREENCHIDO contra o tipo NUMERO e os limites do campo,
 * com a mensagem do servidor; "" quando passa. Limite ilegivel gravado por
 * fora e' ignorado, como no servidor.
 */
export function erroDeLimiteNoEnvio(campo, valor) {
  const label = campo.label || campo.tipo;
  const regras = campo.validacao && typeof campo.validacao === "object" ? campo.validacao : {};

  if (campo.tipo === "NUMERO") {
    const texto = String(valor ?? "").trim();
    if (!NUMERO_DECIMAL.test(texto)) return `${label}: deve ser um número.`;
    return erroDoIntervaloNumerico(campo, Number(texto));
  }

  if (!TIPOS_COM_LIMITE_DE_TAMANHO.includes(campo.tipo) || typeof valor !== "string") return "";
  // Sem o trim, "     " passava num minimo de 5; conta em code points, como o servidor.
  const tamanho = [...valor.trim()].length;
  const minimo = inteiroDoLimite(regras.min_len, 0);
  const maximo = inteiroDoLimite(regras.max_len, 0);
  if (minimo !== null && tamanho < minimo) return `${label}: mínimo ${minimo} caracteres.`;
  if (maximo !== null && tamanho > maximo) return `${label}: máximo ${maximo} caracteres.`;
  return "";
}
